/**
 * window.confirm の代わりに共通の ConfirmDialog を Promise で使うフック。
 * ブラウザの確認ポップアップは Claude 内ブラウザなど一部の環境で出ない (押せない) ため、削除・破棄などの確認は
 * 必ずこのフックの独自モーダルで出す (利用者指示 2026-10-30)。
 *
 *   const { confirm, dialog } = useConfirmDialog();
 *   if (!(await confirm({ title: 'このロゴを削除しますか？', confirmLabel: '削除する' }))) return;
 *   ...
 *   return <>...{dialog}</>;
 */
import { useCallback, useRef, useState, type ReactNode } from 'react';
import { ConfirmDialog } from './ConfirmDialog';

export interface ConfirmOptions {
  title: string;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

export function useConfirmDialog() {
  const [options, setOptions] = useState<ConfirmOptions | null>(null);
  const resolverRef = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    resolverRef.current?.(ok);
    resolverRef.current = null;
    setOptions(null);
  }, []);

  const confirm = useCallback((next: ConfirmOptions): Promise<boolean> => {
    // 前の確認が開いたままなら、それは「キャンセル」として閉じる
    resolverRef.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
      setOptions(next);
    });
  }, []);

  const dialog: ReactNode = options ? (
    <ConfirmDialog
      isOpen
      title={options.title}
      message={options.message ?? ''}
      confirmLabel={options.confirmLabel ?? '確認'}
      cancelLabel={options.cancelLabel ?? 'キャンセル'}
      variant={options.variant ?? 'danger'}
      onConfirm={() => settle(true)}
      onCancel={() => settle(false)}
    />
  ) : null;

  return { confirm, dialog };
}
